import React, { useState, useMemo, useRef } from 'react';
import '../styles/regex.css'

const App = () => {
  const [regexInput, setRegexInput] = useState('');
  const [flags, setFlags] = useState({
    g: true,
    i: false,
    m: false,
    u: false,
    s: false
  });
  const [testString, setTestString] = useState('Hello world! Regular expressions are powerful. 123 numbers.');
  const contentEditableRef = useRef(null);

  // Parse regex and get matches
  const { matches, error, highlightedRanges } = useMemo(() => {
    let error = null;
    let matches = [];
    let highlightedRanges = [];

    try {
      if (regexInput.trim() === '') {
        return { matches: [], error: null, highlightedRanges: [] };
      }

      // Build flag string
      const flagString = Object.entries(flags)
        .filter(([_, enabled]) => enabled)
        .map(([flag]) => flag)
        .join('');

      // Create regex
      const regex = new RegExp(regexInput, flagString);

      // Find all matches
      if (flags.g) {
        let match;
        const tempMatches = [];
        const lastIndex = regex.lastIndex;
        while ((match = regex.exec(testString)) !== null) {
          tempMatches.push({
            index: match.index,
            match: match[0],
            groups: match.slice(1)
          });
          if (match.index === regex.lastIndex) {
            regex.lastIndex++;
          }
        }
        regex.lastIndex = lastIndex;
        matches = tempMatches;
      } else {
        const match = regex.exec(testString);
        if (match) {
          matches = [{
            index: match.index,
            match: match[0],
            groups: match.slice(1)
          }];
        }
      }

      // Create highlighted ranges
      highlightedRanges = matches.map(match => ({
        start: match.index,
        end: match.index + match.match.length,
        match: match.match,
        groups: match.groups
      }));

    } catch (e) {
      error = e.message;
      highlightedRanges = [];
    }

    return { matches, error, highlightedRanges };
  }, [regexInput, flags, testString]);

  const toggleFlag = (flag) => {
    setFlags(prev => ({
      ...prev,
      [flag]: !prev[flag]
    }));
  };

  // Render highlighted test string (for contentEditable)
  const renderHighlightedString = () => {
    if (highlightedRanges.length === 0) {
      return testString;
    }

    const parts = [];
    let lastIndex = 0;

    const sortedRanges = [...highlightedRanges].sort((a, b) => a.start - b.start);

    sortedRanges.forEach((range, index) => {
      if (range.start > lastIndex) {
        parts.push(testString.substring(lastIndex, range.start));
      }

      parts.push(
        <mark
          key={`Совпадение-${index}`}
          style={{
            backgroundColor: '#dbeafe',
            borderRadius: '0.25rem',
            padding: '0 0.125rem',
            color: '#1e40af'
          }}
          title={`Совпадение ${index + 1}: "${range.match}"`}
        >
          {testString.substring(range.start, range.end)}
        </mark>
      );

      lastIndex = range.end;
    });

    if (lastIndex < testString.length) {
      parts.push(testString.substring(lastIndex));
    }

    return parts;
  };

  // SVG Icons as inline JSX
  const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.35-4.35"/>
    </svg>
  );

  const AlertCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  );

  const PlayIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Отладчик регулярных выражений</h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="space-y-6">
          {/* Regex Input Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <SearchIcon />
              <h2 className="text-xl font-semibold text-gray-800 ml-2">Регулярное выражение</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Паттерн
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={regexInput}
                    onChange={(e) => setRegexInput(e.target.value)}
                    placeholder="Введите регулярное выражение..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-mono"
                  />

                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Флаги
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(flags).map(([flag, enabled]) => (
                    <button
                      key={flag}
                      onClick={() => toggleFlag(flag)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        enabled
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={`Flag ${flag}: ${flag === 'g' ? 'Global' : flag === 'i' ? 'Case-insensitive' : flag === 'm' ? 'Multiline' : flag === 'u' ? 'Unicode' : 'DotAll'}`}
                    >
                      {flag}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircleIcon />
                  <span className="ml-2 text-sm">{error}</span>
                </div>
              )}

              {regexInput && !error && (
                <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  <CheckCircleIcon />
                  <span className="ml-2 text-sm">Valid regular expression</span>
                </div>
              )}
            </div>
          </div>

          {/* Test String Section — contentEditable */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <PlayIcon />
              <h2 className="text-xl font-semibold text-gray-800 ml-2">Строка для теста</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">

                </label>
                <div
                  ref={contentEditableRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => {
                    // Safely extract plain text
                    const plainText = e.target.textContent || '';
                    setTestString(plainText);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertText', false, text);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none min-h-[120px] outline-none"
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: '1.5'
                  }}
                >
                  {renderHighlightedString()}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {highlightedRanges.length > 0
                    ? `Сопадений найдено: ${highlightedRanges.length}`
                    : regexInput ? 'Совпадений не найдено' : 'Введите выражение выше чтобы подсветить совпадения'}
                </div>
              </div>
            </div>
          </div>

          {/* Matches Info Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <SearchIcon />
              <span className="ml-2">Детали совпадения</span>
            </h2>

            {matches.length === 0 ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <SearchIcon />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  {error ? 'Неверное выражение!' : 'Совпадений не найдено'}
                </h3>
                <p className="text-gray-500 text-sm">
                  {error
                    ? 'Исправьте регулярное выражение выше чтобы увидеть список совпадений'
                    : 'Введите регулярное выражение чтобы увидеть детали'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {matches.map((match, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="font-medium text-gray-800">
                        Совпадение {index + 1}: <span className="font-mono bg-gray-200 px-2 py-1 rounded text-blue-700">"{match.match}"</span>
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">Позиция: {match.index}–{match.index + match.match.length}</p>
                    </div>

                    <div className="p-4">
                      <div className="mb-3">
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Полное совпадение
                        </span>
                        <code className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
                          {match.match}
                        </code>
                      </div>

                      {match.groups.length > 0 ? (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">Группы:</h4>
                          <div className="space-y-2">
                            {match.groups.map((group, groupIndex) => (
                              <div key={groupIndex} className="flex items-center">
                                <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded mr-2">
                                  Группа {groupIndex + 1}
                                </span>
                                <code className="font-mono bg-gray-100 px-2 py-1 rounded text-gray-800">
                                  {group === undefined ? 'неопределено' : `"${group}"`}
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Групп не найдено</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
